import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Moon, CheckCircle2, CalendarArrowUp, ListChecks, X, Clock } from "lucide-react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { api } from "../lib/api";

/**
 * Banner "Come è andata la giornata?" — appare in Home/Agenda quando esiste
 * una notifica app_notifications con `kind='end_of_day_recap'` non letta.
 *
 * Elementi:
 *   • Lista compatta dei cicli non completati (max 10 visibili, poi "+N")
 *     con checkbox per la conferma singola (completa il ciclo).
 *   • Tre CTA:
 *       - Completa tutti (AlertDialog conferma)
 *       - Rimanda tutto a domani (AlertDialog conferma)
 *       - Gestisci uno per uno → /cicli/aperti?date=<oggi>
 *   • Dismiss button (marca la notifica come letta).
 *
 * onResolved(kind, res) → callback per la pagina genitore (refresh).
 */
export default function EndOfDayBanner({ onResolved }) {
  const [notif, setNotif] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  // Load unread `end_of_day_recap` + hydrate the referenced cycles.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await api.notifList({ unread_only: true, kind: "end_of_day_recap", limit: 5 });
        if (!alive) return;
        const n = (rows || [])[0];
        if (!n) { setNotif(null); setCycles([]); return; }
        setNotif(n);
        const ids = (n.meta?.task_ids || []).slice(0, 20);
        if (ids.length === 0) { setCycles([]); return; }
        // Hydrate tasks via bulk fetch — fall back to per-id
        const tasks = [];
        for (const tid of ids) {
          try {
            const t = await api.getTask(tid);
            if (t && t.status !== "completed" && t.status !== "cancelled") tasks.push(t);
          } catch {}
        }
        if (alive) setCycles(tasks);
      } catch { if (alive) { setNotif(null); setCycles([]); } }
    })();
    return () => { alive = false; };
  }, [tick]);

  if (!notif) return null;

  const meta = notif.meta || {};
  const dateStr = meta.date || new Date().toISOString().slice(0, 10);
  const count = cycles.length || meta.cycles_count || 0;
  const dateEuro = (() => {
    try { const d = new Date(dateStr); return d.toLocaleDateString("it-IT"); } catch { return dateStr; }
  })();

  async function completeOne(taskId) {
    setBusy(true);
    try {
      await api.completeTask(taskId);
      setCycles((c) => c.filter((t) => t.id !== taskId));
      toast.success("Ciclo completato.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel completamento.");
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirmAll() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.endOfDayConfirmAll({ date: dateStr });
      toast.success(`${res.completed} cicli completati.`);
      try { await api.notifRead(notif.id); } catch {}
      setNotif(null);
      onResolved?.("confirm", res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel completamento.");
    } finally { setBusy(false); }
  }

  async function handlePostponeAll() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.endOfDayPostponeAll({ date: dateStr });
      const d = new Date(res.target_date);
      toast.success(`${res.postponed} cicli rimandati al ${d.toLocaleDateString("it-IT")}.`);
      try { await api.notifRead(notif.id); } catch {}
      setNotif(null);
      onResolved?.("postpone", res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nello spostamento.");
    } finally { setBusy(false); }
  }

  async function handleDismiss() {
    try { await api.notifRead(notif.id); } catch {}
    setNotif(null);
    onResolved?.("dismiss");
  }

  return (
    <section
      className="pxos-card p-5 sm:p-6 border-2"
      style={{ borderColor: "var(--pxos-primary-accent, #95BAEF)", background: "linear-gradient(140deg, #F6FAFE, #EEF4FE)" }}
      data-testid="end-of-day-banner"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 h-10 w-10 rounded-full grid place-items-center shrink-0"
             style={{ background: "var(--pxos-primary-accent, #95BAEF)", color: "white" }}>
          <Moon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-mono uppercase tracking-widest opacity-70">Fine giornata · {dateEuro}</div>
          <h2 className="mt-1 font-display text-xl sm:text-2xl font-semibold" data-testid="end-of-day-title">
            Come è andata la giornata?
          </h2>
          <p className="mt-1.5 text-sm opacity-85" data-testid="end-of-day-body">
            Hai ancora <strong data-testid="end-of-day-count">{count}</strong>{" "}
            {count === 1 ? "attività non confermata" : "attività non confermate"}.
          </p>

          {cycles.length > 0 && (
            <ul className="mt-3 space-y-1.5" data-testid="end-of-day-list">
              {cycles.slice(0, 10).map((t) => (
                <li key={t.id} className="flex items-center gap-2 text-sm" data-testid={`eod-item-${t.id}`}>
                  <Checkbox
                    checked={false}
                    onCheckedChange={() => completeOne(t.id)}
                    disabled={busy}
                    aria-label="Completa"
                    data-testid={`eod-item-check-${t.id}`}
                  />
                  {t.scheduled_time && (
                    <span className="inline-flex items-center gap-1 text-xs font-mono opacity-70">
                      <Clock size={10} />{String(t.scheduled_time).slice(0, 5)}
                    </span>
                  )}
                  <span className="truncate">{t.title || "(senza titolo)"}</span>
                </li>
              ))}
              {cycles.length > 10 && (
                <li className="text-xs opacity-70 pl-6">… e altri {cycles.length - 10} cicli.</li>
              )}
            </ul>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button disabled={busy} data-testid="end-of-day-confirm-all-btn">
                  <CheckCircle2 size={14} className="mr-1.5" />
                  Completa tutti
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="eod-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Completare tutti i cicli aperti di oggi?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Segnerò come <strong>completati</strong> i {count} cicli ancora aperti in agenda oggi.
                    Verrà scritto un audit log per ciascuno.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="eod-confirm-cancel">Annulla</AlertDialogCancel>
                  <AlertDialogAction onClick={handleConfirmAll} data-testid="eod-confirm-ok">
                    Sì, completa tutti
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" disabled={busy} data-testid="end-of-day-postpone-all-btn">
                  <CalendarArrowUp size={14} className="mr-1.5" />
                  Rimanda tutto a domani
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="eod-postpone-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Rimandare tutto a domani?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Sposto {count} cicli al giorno successivo mantenendo l’orario invariato.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="eod-postpone-cancel">Annulla</AlertDialogCancel>
                  <AlertDialogAction onClick={handlePostponeAll} data-testid="eod-postpone-ok">
                    Sì, rimanda a domani
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <Link
              to={`/cicli/aperti?date=${dateStr}`}
              onClick={handleDismiss}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium border transition hover:bg-white/70"
              style={{ borderColor: "var(--pxos-border)", color: "var(--pxos-text-dark)" }}
              data-testid="end-of-day-manage-one-btn"
            >
              <ListChecks size={14} />
              Gestisci uno per uno
            </Link>
          </div>
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          className="p-1.5 rounded-full hover:bg-white/60 opacity-70 hover:opacity-100"
          aria-label="Chiudi"
          data-testid="end-of-day-dismiss-btn"
        >
          <X size={16} />
        </button>
      </div>
    </section>
  );
}
